import { useMemo } from 'react'
import { LayerCard } from '@cloudflare/kumo'
import { monthGridDays } from '../lib/calendar-time'
import { useTranslation } from '../i18n'
import type { BuildingCalendarResponse } from '@hub-wifi/shared'

interface BuildingCalendarMonthViewProps {
  /** Any date inside the desired month (YYYY-MM-DD in the building's timezone). */
  date: string
  /** The month-range calendar payload (already fetched by the parent page). */
  data: BuildingCalendarResponse
  /** Called with the YMD of the day the user clicked to drill into Day view. */
  onDrillIntoDay: (ymd: string) => void
  loading?: boolean
}

/** Weekday short-name keys (Mon..Sun) for i18n lookup. */
const WEEKDAY_SHORT_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

/**
 * Month-view renderer for the building calendar.
 *
 * Renders a Monday-start 7-column month grid (5 or 6 weeks) with each day-cell
 * showing the day number and, when non-zero, a small badge with the count of
 * events that fall on that day in the building's timezone. Out-of-month cells
 * (leading/trailing padding) are visually dimmed but still clickable.
 *
 * Clicking any day cell invokes `onDrillIntoDay(ymd)` so the parent page can
 * switch to Day view focused on that date.
 *
 * Event-day bucketing uses `Intl.DateTimeFormat` with the building's timezone
 * to bucket each event's `startAt` to its tz-local YMD — this keeps the count
 * correct even for events that span midnight in UTC.
 */
export function BuildingCalendarMonthView(props: BuildingCalendarMonthViewProps) {
  const { date, data, onDrillIntoDay } = props
  const timezone = data.buildingTimezone
  const { t } = useTranslation()

  const cells = useMemo(() => monthGridDays(date, timezone), [date, timezone])

  const todayYmd = useMemo(
    () =>
      new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date()),
    [timezone],
  )

  // Flatten rooms[].events[] then bucket by tz-local YMD via Intl formatter.
  //
  // Bucket events by their start-day in the building tz. Multi-day events
  // (rare for meeting rooms — typically a single contiguous slot) appear only
  // on their start day. To bucket across the full [start, end] range, iterate
  // inclusively from startYmd to endYmd; deferred until product calls for it.
  const countsByDay = useMemo(() => {
    const m = new Map<string, number>()
    const dayFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    for (const room of data.rooms) {
      for (const ev of room.events) {
        const startDate = new Date(ev.startAt)
        if (Number.isNaN(startDate.getTime())) continue
        const ymd = dayFmt.format(startDate)
        m.set(ymd, (m.get(ymd) ?? 0) + 1)
      }
    }
    return m
  }, [data.rooms, timezone])

  return (
    <LayerCard>
      <LayerCard.Primary>
        <div
          role="grid"
          aria-label={t('buildingCalendar.monthGridLabel')}
          data-testid="calendar-month-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '1px',
            background: 'var(--color-kumo-line)',
            border: '1px solid var(--color-kumo-line)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {/* Header row: 7 weekday short names */}
          {WEEKDAY_SHORT_KEYS.map((k) => (
            <div
              key={k}
              role="columnheader"
              style={{
                background: 'var(--color-kumo-base)',
                padding: '8px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '12px',
              }}
            >
              {t(`buildingCalendar.weekdayShort.${k}`)}
            </div>
          ))}

          {/* Day cells */}
          {cells.map((cell) => {
            const count = countsByDay.get(cell.ymd) ?? 0
            const isToday = cell.ymd === todayYmd
            const dayNum = Number(cell.ymd.slice(8, 10))
            return (
              <button
                key={cell.ymd}
                type="button"
                onClick={() => onDrillIntoDay(cell.ymd)}
                data-testid={`calendar-month-cell-${cell.ymd}`}
                aria-label={t('buildingCalendar.monthCellLabel')
                  .replace('{date}', cell.ymd)
                  .replace('{count}', String(count))}
                style={{
                  background: isToday ? 'var(--color-kumo-info-tint)' : 'var(--color-kumo-base)',
                  border: 'none',
                  textAlign: 'left',
                  padding: '8px',
                  cursor: 'pointer',
                  minHeight: '88px',
                  opacity: cell.inCurrentMonth ? 1 : 0.45,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                <span
                  style={{
                    fontWeight: isToday ? 700 : 500,
                    color: isToday ? 'var(--text-color-kumo-link)' : undefined,
                  }}
                >
                  {dayNum}
                </span>
                {count > 0 && (
                  <span
                    data-testid={`calendar-month-count-${cell.ymd}`}
                    style={{
                      fontSize: '11px',
                      background: 'var(--text-color-kumo-link)',
                      color: 'white',
                      borderRadius: '10px',
                      padding: '2px 8px',
                      width: 'fit-content',
                      fontWeight: 600,
                    }}
                  >
                    {t(
                      count === 1
                        ? 'buildingCalendar.bookingsCount.one'
                        : 'buildingCalendar.bookingsCount.other',
                    ).replace('{count}', String(count))}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </LayerCard.Primary>
    </LayerCard>
  )
}
